import { Type, Static } from '@sinclair/typebox';
import { AttachmentSchema } from '../../shared/attachment/attachment.schema';

export const EmailAttendeeSchema = Type.Object({
  email: Type.String({ description: 'The email address of the attendee.' }),
  display_name: Type.Optional(Type.String({ description: 'The display name of the attendee if any.' })),
});

export const EmailSchema = Type.Object({
  object: Type.Literal('Email'),
  id: Type.String({ description: 'The ID of the email.' }),
  message_id: Type.String({ description: 'The Message-ID header value of the email. Use `id` in Unipile requests.' }),
  thread_id: Type.Optional(
    Type.String({
      description:
        'The thread identifier. For IMAP, Unipile uses the server-provided thread ID when available. Otherwise, it uses the entire References header, or In-Reply-To, or Message-ID, in that order. A header-derived value may differ between messages in the same conversation and may be absent when none of these headers exists.',
    }),
  ),
  body: Type.String({
    description:
      'The complete body of the email: HTML when available, otherwise plain text. An empty string when the email has no body or meta_only=true.',
  }),
  body_plain: Type.String({
    description:
      'The complete plain-text body of the email. Uses the original plain-text version when available, otherwise text converted from HTML. For Outlook, text is converted from the HTML body returned by Microsoft Graph, or taken directly from its plain-text body. An empty string when the email has no body or meta_only=true.',
  }),
  snippet: Type.String({
    description: 'A short preview of the email, not its complete plain-text body. Use body_plain for the full text.',
  }),
  subject: Type.String({ description: 'The subject of the email.' }),
  headers: Type.Array(
    Type.Object({
      name: Type.String({ description: 'Name of the header.' }),
      value: Type.String({ description: 'Value of the header' }),
    }),
    {
      description:
        'List of headers. A single email can sometimes have multiple headers with the same key name. Unipile adds all of the headers to the headers array without merging or de-duplicating the data. When the headers contain encoded data, Unipile adds it to the headers array without decoding it.',
    },
  ),
  from: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'The list of attendees that the email was sent from.' })),
  to: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'The list of attendees that the email was sent to.' })),
  cc: Type.Array(EmailAttendeeSchema, {
    description: "The list of attendees that the email was CC'd to. An empty array when none are available.",
  }),
  bcc: Type.Array(EmailAttendeeSchema, {
    description: "The list of attendees that the email was BCC'd to. An empty array when none are available.",
  }),
  reply_to: Type.Array(EmailAttendeeSchema, {
    description: 'The list of attendees that the replies should be sent to. An empty array when none are available.',
  }),
  date: Type.String({ description: 'The date the email was sent. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).' }),
  attachments: Type.Array(AttachmentSchema, { description: 'The attachments of the email.' }),
  folders: Type.Array(Type.String(), {
    description:
      'The IDs of folders the email is in. For Gmail, the IDs of labels assigned to the email, excluding the UNREAD system label, which is exposed through is_unread instead.',
  }),
  categories: Type.Optional(
    Type.Array(Type.String({ description: '(Outlook only) A category assigned to the email.' }), {
      description: 'List of categories assigned to the email.',
    }),
  ),
  is_unread: Type.Boolean({ description: 'Is the email unread. For Gmail, this reflects the UNREAD system label.' }),
});

export type Email = Static<typeof EmailSchema>;
export type EmailAttendee = Static<typeof EmailAttendeeSchema>;
